/**
 * Centralized Audio Unlock Utility
 *
 * iOS Safari (especially PWA standalone mode) requires audio to be "unlocked"
 * by a user gesture before any programmatic audio playback works. This module
 * creates a shared AudioContext and plays a silent buffer on first user
 * interaction, enabling all subsequent Audio().play() calls app-wide.
 *
 * Usage:
 *   - Call `registerGlobalAudioUnlock()` once at app startup to attach
 *     touchstart/click listeners that auto-unlock on first interaction.
 *   - Call `ensureAudioUnlocked()` before any audio .play() as a safety net.
 */

/** @type {AudioContext|null} Shared AudioContext instance */
let audioContext = null;

/** Whether audio has been unlocked for this session */
let unlocked = false;

/**
 * Unlock audio playback by creating an AudioContext and playing a silent buffer.
 * Also plays a silent HTML5 Audio element to unlock that pathway.
 * Safe to call multiple times — only executes once.
 */
export function unlockAudio() {
    if (unlocked) return;
    unlocked = true;

    try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
            audioContext = new AudioCtx();
            const buffer = audioContext.createBuffer(1, 1, 22050);
            const source = audioContext.createBufferSource();
            source.buffer = buffer;
            source.connect(audioContext.destination);
            source.start(0);

            // Resume context if it was suspended (iOS Safari requirement)
            if (audioContext.state === 'suspended') {
                audioContext.resume().catch(() => { });
            }
        }

        // Also unlock HTML5 Audio element pathway
        const silence = new Audio(
            'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA='
        );
        silence.volume = 0;
        silence.play().catch(() => { });
    } catch {
        /* ignore — best effort */
    }
}

/**
 * Ensure audio is unlocked before attempting playback.
 * Call this as a safety net before any .play() call.
 * If already unlocked, this is a no-op.
 */
export function ensureAudioUnlocked() {
    if (!unlocked) {
        unlockAudio();
    }

    // Resume AudioContext if it was suspended (e.g., after backgrounding)
    if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume().catch(() => { });
    }
}

/**
 * Check if audio has been unlocked this session.
 * @returns {boolean}
 */
export function isAudioUnlocked() {
    return unlocked;
}

/**
 * Register global audio unlock listeners at the app level.
 * Attaches touchstart and click listeners to the document that unlock
 * audio on first user interaction, then remove themselves.
 *
 * Call this once during app initialization.
 */
export function registerGlobalAudioUnlock() {
    if (unlocked) return;

    const handler = () => {
        unlockAudio();
        // Remove listeners after first successful unlock
        document.removeEventListener('touchstart', handler, true);
        document.removeEventListener('click', handler, true);
        document.removeEventListener('pointerdown', handler, true);
    };

    // Use capture phase to fire before any other handlers
    document.addEventListener('touchstart', handler, { capture: true, passive: true });
    document.addEventListener('click', handler, { capture: true });
    document.addEventListener('pointerdown', handler, { capture: true });
}
